import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat';
import { Button, Divider, Link, Tooltip } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import { FormInput } from 'src/components/molecules/FormInput';
import { SpinnerButton } from 'src/components/molecules/SpinnerButton';
import { handleErrorToast } from 'src/components/molecules/Toast';
import { SmallText } from 'src/components/public/OPRLandingPage/OPRLandingPage.styles';
import { useIsMobile } from 'src/hooks/useIsMobile';
import {
  createReportingSession,
  getCreateReportingSessionLoading,
  getCurrentUser,
} from 'src/reducers/Reporting.slice';
import { AppDispatch } from 'src/store';
import theme from 'src/theme/theme';
import { FormInputVariant, FormState } from 'src/types';
import {
  ACCOUNT_ACTIVATION_SOURCE,
  REPORTING_ACCOUNT_ACTIVATION_STEP,
  trackAccountActivation,
} from 'src/utils/analytics/track/reporting';
import { isEmail } from 'validator';
import { LoginContainer } from '../ReportingLoginContainer/ReportingLoginContainer';
import {
  ButtonContainer,
  ButtonText,
  ButtonWrapper,
  CreateAccountButtonContainer,
  DividerContainer,
  DividerWrapper,
  FormContainer,
  InputsContainer,
  LinkWrapper,
} from './ReportingLogin.styles';

export enum RedirectOptions {
  CONNECT = 'connect',
  EXTERNAL = 'external',
}

interface FormValues {
  email: string;
  password: string;
}

const INTERCOM_HELP_PAGE =
  'https://intercom.help/vault-platform-384e32b6cea0/en/';

const ALLOWED_REDIRECT_HOSTS = ['dilligent-coi.vercel.app'];
// this is a temporary url whilst not in vault systems

const ReportingLogin = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useSelector(getCurrentUser);
  const createReportingSessionLoading = useSelector(
    getCreateReportingSessionLoading,
  );

  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const code = searchParams.get('code');
  const redirect = searchParams.get('redirect');
  const redirectUri = searchParams.get('redirect_uri');

  const onSubmit = async (data: FormValues) => {
    const { payload } = await dispatch(
      createReportingSession({
        username: data.email,
        password: data.password,
      }),
    );

    const error =
      (payload && 'errors' in payload && payload?.errors?.[0]) || undefined;

    if (error?.code === 'validationError.session.accountLockedPermanently') {
      handleErrorToast({
        toastMessage: t('reporting.ReportingLogin.accountLockedPermanently'),
      });
      return;
    }

    if (error?.code === 'validationError.session.accountLocked') {
      handleErrorToast({
        toastMessage: t('reporting.ReportingLogin.accountLocked'),
      });
      return;
    }

    if (error?.code === 'validationError.session.invalidCredentials') {
      handleErrorToast({
        toastMessage: t('reporting.ReportingLogin.usernameLoginFailed'),
      });
      return;
    }

    if (error?.code === 'validationError.session.passwordResetRequired') {
      handleErrorToast({
        toastMessage: t('reporting.ReportingLogin.passwordResetRequired'),
      });
      // TODO enforce password reset once flow built for reporters - to follow with account management tickets
      return;
    }

    if (error?.message) {
      handleErrorToast({ toastMessage: error.message });
      return;
    }
  };

  const {
    register,
    handleSubmit,
    formState: { isDirty, isValid, errors },
  } = useForm<FormValues>({
    mode: 'onChange',
  });

  const emailState = errors.email
    ? FormState.ERROR
    : isValid
      ? FormState.SUCCESS
      : FormState.DEFAULT;

  const isValidRedirectUri = (uri: string): boolean => {
    try {
      const url = new URL(uri);
      return ALLOWED_REDIRECT_HOSTS.includes(url.hostname);
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (currentUser.session) {
      if (redirect === RedirectOptions.CONNECT) {
        navigate(`/reporting/connect?code=${code}`, {
          state: {
            isPostLoginRedirect: true,
          },
        });
        return;
      }
      if (redirect === RedirectOptions.EXTERNAL && redirectUri) {
        if (isValidRedirectUri(redirectUri)) {
          const url = new URL(redirectUri);
          url.searchParams.append('token', currentUser.session.token);
          window.location.href = url.toString();
          return;
        }
      }

      navigate('/reporting/reports');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.session, navigate]);

  const handleClick = () => {
    navigate('/reporting/activate');

    trackAccountActivation({
      step_description: REPORTING_ACCOUNT_ACTIVATION_STEP.REGISTER_FOR_ACCOUNT,
      source: ACCOUNT_ACTIVATION_SOURCE.USER_PORTAL,
    });
  };

  return (
    <LoginContainer>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <InputsContainer>
          <FormInput
            variant={FormInputVariant.INPUT}
            state={emailState}
            label={t('reporting.ReportingLogin.emailLabel')}
            placeholder={'email@address.com'}
            data-testid="user-login-email-input"
            type="text"
            {...register('email', {
              required: true,
              validate: (value) => isEmail(value),
            })}
          />
          <FormInput
            variant={FormInputVariant.INPUT}
            state={FormState.DEFAULT}
            label={t('reporting.ReportingLogin.passwordLabel')}
            data-testid="user-login-password-input"
            type="password"
            placeholder={t('reporting.ReportingLogin.passwordPlaceholder')}
            {...register('password', {
              required: true,
            })}
          />
        </InputsContainer>

        <LinkWrapper>
          <Link data-testid="password-input" to="/reporting/reset" size="small">
            {t('reporting.ReportingLogin.forgotPassword')}
          </Link>
        </LinkWrapper>

        <ButtonContainer isMobile={isMobile}>
          <SpinnerButton
            variant="primary"
            size="medium"
            type="submit"
            width="100%"
            disabled={!isDirty || !isValid}
            loading={createReportingSessionLoading}
            data-testid="user-login-cta"
          >
            <>
              <StyledIconInButton
                size="medium"
                position="left"
                className="uil uil-plus"
              />
              {t('reporting.ReportingLogin.loginButton')}
            </>
          </SpinnerButton>
        </ButtonContainer>
      </FormContainer>
      <CreateAccountButtonContainer>
        {' '}
        <DividerContainer>
          <DividerWrapper>
            <Divider variant="horizontal" />
          </DividerWrapper>
          <SmallText>{t('public.CreatePublicReport.or')}</SmallText>
          <DividerWrapper>
            <Divider variant="horizontal" />
          </DividerWrapper>
        </DividerContainer>
        <ButtonWrapper>
          <Button
            variant="secondary"
            size="medium"
            width="100%"
            data-testid="no-account-cta"
            onClick={handleClick}
          >
            <>
              <ButtonText>
                {' '}
                {t('reporting.ReportingLogin.createAccountCTA')}
              </ButtonText>
              <StyledIcon
                className="uil uil-info-circle"
                color={theme.newPalette.neutral.N900}
                size="24px"
                ref={setRoot}
                onMouseOver={() => {
                  setTooltipVisible(true);
                }}
                onMouseOut={() => setTooltipVisible(false)}
              />
            </>
          </Button>
        </ButtonWrapper>
      </CreateAccountButtonContainer>

      <LinkWrapper>
        <Link size="small" target="_blank" href={INTERCOM_HELP_PAGE}>
          {t('public.CreatePublicReport.helpPage')}
        </Link>
      </LinkWrapper>

      {tooltipVisible && root && (
        <Tooltip
          text={t('reporting.ReportingLogin.tooltip')}
          variant="up"
          root={root}
          width="300px"
        />
      )}
    </LoginContainer>
  );
};

export default ReportingLogin;
